/**
 * @summary - Component that Provides Look up functionality
 * @autor vengateshwaran k
 * @since 2024-06-21
 */
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import {
  FormControl,
  MenuItem,
  Typography,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Grid,
  FormHelperText,
  Paper,
} from "@mui/material";
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Search,
  Clear,
} from "@mui/icons-material";

const CustomTextFieldWithSearch = ({
  label,
  id,
  onClear,
  clearField,
  setClearField,
  value,
  options,
  onChange,
  isMandatoryField = false,
  isDisable,
  disabled = false,
  error,
  errorMessage,
  autoFocus = false,
  helperText,
  name,
  disableplaceholder = false,
  hideSearch = false
}) => {
  const viewEnabled = useSelector(
    (state) => state.mandatoryFields.isViewEnabled
  );
  const mandatoryRequiredButton = useSelector(
    (state) => state.mandatoryFields.isMandatoryRequiredEnabled
  );

  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [optionsPerPage, setOptionsPerPage] = useState([]);
  const pageSize = 5;
  const [searchTerm, setSearchTerm] = useState("");
  const [searchText, setSearchText] = useState("");
  const [selectValue, setSelectValue] = useState(
    value || { label: "", value: "" }
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [inputFocused, setInputFocused] = useState(false); // Track focus state
  const inputRef = useRef(null);

  const filteredOptions = useMemo(() => {
    if (options?.status !== "error") {
      return options
        ?.filter((option) =>
          option?.label?.toLowerCase().includes(searchTerm?.toLowerCase())
        )
        ?.sort((a, b) => a.label.localeCompare(b.label)); // Sort in ascending order
    }
    return [];
  }, [options, searchTerm]);


  useEffect(() => {
    // Reset to first page and recalculate options per page when searchTerm changes
    setPageNumber(1);
    setTotalPages(Math.ceil(filteredOptions?.length / pageSize));
  }, [filteredOptions]);

  useEffect(() => {
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = pageNumber * pageSize;
    setOptionsPerPage(filteredOptions?.slice(startIndex, endIndex));
  }, [filteredOptions, pageNumber]);

  useEffect(() => {
    if (value) {
      setSelectValue(value); // Set the select value when a valid value is passed
      setSearchTerm(value?.label); // Update the search term as well
    } if (clearField) {
      clearSearch(); // Clear the search only if clearField is true
    }
  }, [value, clearField]);

  useEffect(() => {
    if (filteredOptions?.length === 0 && !inputFocused) {

      if (!value) {
        clearSearch();
      }
    }
  }, [filteredOptions, inputFocused, value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (inputFocused) { // Only handle key events when the input is focused
        if (menuOpen) {
          switch (event.key) {
            case "ArrowUp":
              event.preventDefault();
              setHighlightedIndex((prevIndex) =>
                prevIndex > 0 ? prevIndex - 1 : 0
              );
              break;
            case "ArrowDown":
              event.preventDefault();
              setHighlightedIndex((prevIndex) =>
                prevIndex < optionsPerPage.length - 1
                  ? prevIndex + 1
                  : optionsPerPage.length - 1
              );
              break;
            case "Enter":
              event.preventDefault();
              if (highlightedIndex >= 0) {
                handleOptionSelect(optionsPerPage[highlightedIndex]);
              }
              break;
            case "ArrowLeft":
              event.preventDefault();
              if (pageNumber > 1) {
                setPageNumber(pageNumber - 1);
              }
              break;
            case "ArrowRight":
              event.preventDefault();
              if (pageNumber < totalPages) {
                setPageNumber(pageNumber + 1);
              }
              break;
            case "Escape":
              event.preventDefault();
              setMenuOpen(false);
              break;
            case "Tab":
              event.preventDefault();
              setMenuOpen(false);
              break;
            default:
              break;
          }
        } else if (event.key === "ArrowDown") {
          event.preventDefault();
          setMenuOpen(true); // Open menu when ArrowDown is pressed and menu is not open
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    menuOpen,
    pageNumber,
    totalPages,
    searchTerm,
    filteredOptions,
    highlightedIndex,
    optionsPerPage,
    inputFocused,
  ]);

  useEffect(() => {
    if (clearField) {
      clearSearch();
    }
  }, [clearField]);


  const handlePreviousPage = (event) => {
    event.stopPropagation();
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const handleNextPage = (event) => {
    event.stopPropagation();
    if (pageNumber < totalPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    if (event.target.value == '') {
      clearSearch();
    }
    const value = event.target.value;
    setSearchTerm(value);
    setSelectValue({ label: value, value: value });
    setPageNumber(1); // Reset page number when search term changes
    setHighlightedIndex(-1);
    if (!disabled && !viewEnabled) {
      setMenuOpen(true);
    }
  };

  const handleOptionSelect = (selectedValue) => {
    setSelectValue(selectedValue);
    onChange(selectedValue, name, isMandatoryField);
    setSearchTerm(selectedValue.label);
    setMenuOpen(false);
    setHighlightedIndex(-1);
    // Focus back to the input field
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleClearSearch = () => {
    if (!clearField && onClear) {
      onClear();
    } else {
      clearSearch();
    }
  };

  const clearSearch = () => {
    const emptyValue = { label: "", value: "" };
    setSelectValue(emptyValue);
    setSearchTerm("");
    setPageNumber(1);
    setMenuOpen(false);
    setHighlightedIndex(-1);
    onChange(emptyValue, name, isMandatoryField); // Call onChange with empty value
    if (setClearField) {
      setClearField(false);
    }
    // Focus back to the input field
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };


  const handleFieldClick = () => {
    if (!disabled && !viewEnabled) {
      if (pageNumber !== 1) {
        setPageNumber(1); // Reset page number to 1
      }
      setMenuOpen(!menuOpen);
    }
  };

  return (
    <>
      <Typography variant="labelSm" color={"text.primary"} component={"label"}>
        {label}
        {isMandatoryField && (
          <Typography component={"span"} color={"error"}>
            *
          </Typography>
        )}
      </Typography>
      <FormControl
        variant="outlined"
        fullWidth
        style={{ position: "relative" }}
        error={error}
        ref={inputRef}
      >
        <OutlinedInput
          id={`${id}-search`}
          value={clearField ? "" : selectValue.label}
          onChange={handleSearchChange}
          placeholder={disableplaceholder === true ? "" : "Please Select"}
          autoFocus={autoFocus}
          name={name}
          endAdornment={
            <>
            {!hideSearch && 
              <InputAdornment position="end">
                <IconButton
                  disabled={
                    disabled ||
                    viewEnabled ||
                    (mandatoryRequiredButton && !isMandatoryField) ||
                    isDisable
                  }
                  onClick={handleFieldClick}
                >
                  <Search />
                </IconButton>
              </InputAdornment>
              }
              {(searchTerm || selectValue.label) && (
                <InputAdornment position="end">
                  <IconButton
                    disabled={
                      disabled ||
                      viewEnabled ||
                      (mandatoryRequiredButton && !isMandatoryField) ||
                      isDisable
                    }
                    onClick={handleClearSearch}
                    edge="end"
                  >
                    <Clear />
                  </IconButton>
                </InputAdornment>
              )}
            </>
          }
          fullWidth
          readOnly={false} // Always allow typing
          onClick={handleFieldClick}
          disabled={
            disabled ||
            viewEnabled ||
            (mandatoryRequiredButton && !isMandatoryField) ||
            isDisable
          }
          error={error}
          onFocus={() => setInputFocused(true)}
          onBlur={() => {
            let searchOptions;
            if (searchText != '') {
              searchOptions = filteredOptions?.filter((option) =>
                //option?.label===searchText
                option?.label?.toLowerCase().includes(searchText?.toLowerCase())
              )
            }
            setInputFocused(false);
            if (filteredOptions?.length === 0 || searchOptions?.length === 0) {

              clearSearch();
              setSearchText('');
            } else {
              const searchOptionsCheck = searchOptions?.filter((option) =>
                //option?.label===searchText
                option?.label?.toLowerCase() === searchText?.toLowerCase()
              )
              if (searchOptionsCheck?.length == 0) setSelectValue({ label: '', value: '' });
            }
          }
          }
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: error ? "red" : undefined,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: error ? "red" : undefined,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: error ? "red" : undefined,
            },
          }}
        />

        {menuOpen &&
          !disabled &&
          !viewEnabled &&
          !isDisable &&
          (!mandatoryRequiredButton || isMandatoryField) && (
            <Paper
              sx={{
                position: "absolute",
                top: "calc(100% + 8px)", // Position below the input field with a small gap
                zIndex: 1500,
                width: "100%",
                maxHeight: "240px", // Adjust height for 5 options (each option ~48px)
                overflowY: "auto",
                mt: 1,
                backgroundColor: "background.paper",
                boxShadow: "rgba(0, 0, 0, 0.16) 0px 1px 4px",
                borderRadius: "4px",
              }}
            >
              {filteredOptions?.length > 0 ? (
                <>
                  {optionsPerPage?.map((option, index) => (
                    <MenuItem
                      key={option.label}
                      onClick={() => handleOptionSelect(option)}
                      selected={highlightedIndex === index}
                      sx={{
                        backgroundColor:
                          highlightedIndex === index
                            ? "rgba(0, 0, 0, 0.08)"
                            : undefined,
                        "&:hover": {
                          backgroundColor: "theme.palette.primary.light",
                        },
                      }}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                  <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      width: "100%",
                      backgroundColor: "theme.palette.background.paper",
                      zIndex: 1,
                    }}
                  >
                    <Grid item>
                      <MenuItem
                        key="previous"
                        disabled={pageNumber === 1}
                        onClick={handlePreviousPage}
                      >
                        <KeyboardArrowLeft />
                      </MenuItem>
                    </Grid>
                    <Grid item>
                      <MenuItem disabled>
                        <Typography>{`Page ${pageNumber} of ${totalPages}`}</Typography>
                      </MenuItem>
                    </Grid>
                    <Grid item>
                      <MenuItem
                        key="next"
                        disabled={pageNumber === totalPages}
                        onClick={handleNextPage}
                      >
                        <KeyboardArrowRight />
                      </MenuItem>
                    </Grid>
                  </Grid>
                </>
              ) : (
                <MenuItem disabled>No match found</MenuItem>
              )}
            </Paper>
          )}

        {error && <FormHelperText>{errorMessage || helperText}</FormHelperText>}
      </FormControl>
    </>
  );
};

export default CustomTextFieldWithSearch;
